import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  Home, DoorOpen, ThermometerSun, ReceiptText, ShoppingBag,
  ChevronLeft, ChevronRight, X, Bell, UserCircle,
  Users, CalendarCheck, DollarSign, FileBarChart, Zap, Package2,
  Warehouse, Beef, Droplets, HeartPulse, Baby, Dna, Handshake,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useFarmModules } from '../../context/FarmModuleContext';
import { canAccessFinancialModules } from '../../utils/roles';
import { version as appVersion } from '../../../package.json';

/**
 * Check if user has access to a feature based on plan limits from API.
 * @param {number} limit - The limit value from planLimits
 *   -1 = unlimited access
 *   0 = no access (show upgrade prompt)
 *   positive = limited access (show upgrade when limit reached)
 * @returns {boolean} - Whether user has any access to the feature
 */
function hasFeatureAccess(limit) {
  return limit !== 0; // 0 = no access, -1 or positive = has access
}

/**
 * Check if feature requires upgrade (limit is 0 or limit reached).
 * @param {number} limit - The limit value
 * @param {number} current - Current usage count
 * @returns {boolean} - Whether to show upgrade prompt/button
 */
function isFeatureBlocked(limit, current = 0) {
  if (limit === 0) return true; // No access at all
  if (limit === -1) return false; // Unlimited
  return current >= limit; // Limit reached
}

/**
 * Render a single navigation item
 */
function renderNavItem(item, collapsed, isMobile, onClose, limits, handleNavClick) {
  const { to, label, Icon, end, limitKey } = item;
  const limit = limitKey ? limits[limitKey] : null;
  const noAccess = limit !== null && !hasFeatureAccess(limit);

  return (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={(e) => handleNavClick(item, e)}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        collapsed
          ? `w-12 h-12 rounded-xl flex items-center justify-center border transition-all duration-150 group relative ${isActive && !noAccess
            ? 'bg-white/20 border-white/25 text-white'
            : noAccess
              ? 'border-transparent text-primary-500/50 cursor-pointer'
              : 'border-transparent text-primary-400 hover:bg-white/10 hover:text-white'
          }`
          : `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${isActive && !noAccess
            ? 'bg-white/20 text-white'
            : noAccess
              ? 'text-primary-400/50 hover:bg-white/5 cursor-pointer'
              : 'text-primary-200 hover:bg-white/10 hover:text-white'
          }`
      }
    >
      {({ isActive }) => (
        <>
          {!collapsed && isActive && !noAccess && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-400 rounded-r-full" />
          )}
          <Icon size={collapsed ? 20 : 18} className={`flex-shrink-0 ${noAccess ? 'opacity-50' : ''}`} />
          {!collapsed && (
            <span className="flex items-center gap-2 flex-1">
              <span className={noAccess ? 'line-through decoration-primary-400/30' : ''}>{label}</span>
              {noAccess && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-bold uppercase tracking-wide">
                  <Zap size={8} />
                  Pro
                </span>
              )}
            </span>
          )}
          {collapsed && noAccess && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center">
              <Zap size={7} className="text-white" />
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ open, collapsed, onClose, onToggleCollapse, onNotifClick }) {
  const { t } = useTranslation();
  const { state } = useApp();
  const { user, planLimits } = useAuth();
  const { hasDairy, hasPoultry } = useFarmModules();
  const navigate = useNavigate();

  // Get limits from API-driven planLimits (from AuthContext/tenant)
  // DB values: -1 = unlimited, 0 = no access, positive = limit
  const limits = {
    employees: planLimits?.employees ?? 0,
    attendance: planLimits?.attendance ?? 0,
    payroll: planLimits?.payroll ?? 0,
    reports: planLimits?.reports ?? 0,
    inventory: planLimits?.inventory ?? 0,
  };

  // Dashboard - rendered separately at the top
  const DASHBOARD_ITEM = { to: '/', label: t('navigation.dashboard'), Icon: Home, end: true };

  // POULTRY section items
  const POULTRY_ITEMS = [
    { to: '/rooms', label: t('navigation.rooms'), Icon: DoorOpen },
    { to: '/incubator', label: t('navigation.incubator'), Icon: ThermometerSun },
  ];

  // FINANCIAL section items (Inventory, Sales, Expenses)
  const FINANCIAL_ITEMS = [
    { to: '/inventory', label: t('navigation.inventory'), Icon: Package2, limitKey: 'inventory', financial: true },
    { to: '/expenses', label: t('navigation.expenses'), Icon: ReceiptText, financial: true },
    { to: '/sales', label: t('navigation.sales'), Icon: ShoppingBag, financial: true },
  ];

  // HR section items (Employees, Attendance, Payroll, Reports)
  const HR_ITEMS = [
    { to: '/employees', label: t('navigation.employees'), Icon: Users, financial: true, limitKey: 'employees' },
    { to: '/attendance', label: t('navigation.attendance'), Icon: CalendarCheck, financial: true, limitKey: 'attendance' },
    { to: '/payroll', label: t('navigation.payroll'), Icon: DollarSign, financial: true, limitKey: 'payroll' },
    { to: '/reports', label: t('navigation.reports'), Icon: FileBarChart, financial: true, limitKey: 'reports' },
  ];

  const NAV_ITEMS = [...POULTRY_ITEMS, ...FINANCIAL_ITEMS, ...HR_ITEMS];

  // Filter out items user can't access due to role
  const accessibleNavItems = NAV_ITEMS.filter((item) => !item.financial || canAccessFinancialModules(user?.role));
  const isMobile = () => window.innerWidth < 1024;

  const handleNavClick = (item, e) => {
    const limit = item.limitKey ? limits[item.limitKey] : null;
    const blocked = limit !== null && !hasFeatureAccess(limit);

    if (blocked) {
      e.preventDefault();
      navigate('/upgrade');
      if (isMobile()) onClose();
    } else if (isMobile()) {
      onClose();
    }
  };

  return (
    <aside
      className={[
        'fixed z-40 top-0 left-0 h-full flex flex-col',
        'bg-primary-900 dark:bg-gray-900',
        'transition-all duration-300 ease-in-out',
        open ? 'translate-x-0' : '-translate-x-full',
        'lg:relative lg:translate-x-0 lg:flex-shrink-0',
        collapsed ? 'lg:w-[72px]' : 'w-64',
        'lg:shadow-[4px_0_24px_rgba(0,0,0,0.15)]',
      ].join(' ')}
    >
      {/* ── Brand ─────────────────────────────────────────────── */}
      <div
        className={`flex items-center h-16 border-b border-primary-800/60 flex-shrink-0 ${collapsed ? 'px-0 justify-center' : 'px-4 gap-3'
          }`}
      >
        <img
          src="/logo-sidebar.svg"
          alt="FarmSmartly"
          className={`flex-shrink-0 rounded-2xl ${collapsed ? 'h-14 w-14' : 'h-14 w-auto'}`}
        />
        
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-white font-bold text-[15px] leading-tight truncate">{t('common.appName')}</p>
            <p className="text-primary-300 text-[10px] leading-tight">{t('common.tagline')} 🌿</p>
          </div>
        )}

        {/* Mobile close button */}
        {!collapsed && (
          <button
            onClick={onClose}
            className="lg:hidden w-7 h-7 rounded-lg flex items-center justify-center text-primary-300 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* ── Navigation ────────────────────────────────────────── */}
      <nav
        className={`flex-1 overflow-y-auto py-3 no-scrollbar ${collapsed ? 'flex flex-col items-center gap-1 px-2' : 'px-2 space-y-0.5'
          }`}
      >
        {/* ── Dashboard (always at top) ── */}
        {renderNavItem(DASHBOARD_ITEM, collapsed, isMobile, onClose, limits, handleNavClick)}

        {/* ── Dairy module section (only when DAIRY is active) ── */}
        {hasDairy && !collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400 px-3 pb-1.5 pt-3">
            {t('navigation.dairy', 'Dairy')}
          </p>
        )}
        {hasDairy && [
          { to: '/dairy/sheds',    label: t('navigation.dairySheds',    'Sheds'),            Icon: Warehouse },
          { to: '/dairy/animals',  label: t('navigation.dairyAnimals',  'Animals'),          Icon: Beef },
          { to: '/dairy/milk',     label: t('navigation.dairyMilk',     'Milk Records'),     Icon: Droplets },
          // Financial — buying/selling milk and seller/customer balances, so gated the same
          // way as the FINANCIAL section below, unlike the rest of the Dairy nav items.
          ...(canAccessFinancialModules(user?.role)
            ? [{ to: '/dairy/trading', label: t('navigation.dairyTrading', 'Milk Trading'), Icon: Handshake }]
            : []),
          { to: '/dairy/health',   label: t('navigation.dairyHealth',   'Health'),           Icon: HeartPulse },
          { to: '/dairy/breeding', label: t('navigation.dairyBreeding', 'Breeding'),         Icon: Dna },
        ].map((item) => {
          const { to, label, Icon } = item;
          return (
            <NavLink
              key={to}
              to={to}
              onClick={() => { if (isMobile()) onClose(); }}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                collapsed
                  ? `w-12 h-12 rounded-xl flex items-center justify-center border transition-all duration-150 group relative ${
                      isActive ? 'bg-white/20 border-white/25 text-white' : 'border-transparent text-primary-400 hover:bg-white/10 hover:text-white'
                    }`
                  : `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                      isActive ? 'bg-white/20 text-white' : 'text-primary-200 hover:bg-white/10 hover:text-white'
                    }`
              }
            >
              {({ isActive }) => (
                <>
                  {!collapsed && isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-400 rounded-r-full" />
                  )}
                  <Icon size={collapsed ? 20 : 18} className="flex-shrink-0" />
                  {!collapsed && <span>{label}</span>}
                </>
              )}
            </NavLink>
          );
        })}

        {/* ── POULTRY section ── */}
        {hasPoultry && !collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400 px-3 pb-1.5 pt-3">
            {t('navigation.poultry', 'Poultry')}
          </p>
        )}
        {hasPoultry && POULTRY_ITEMS.map((item) => renderNavItem(item, collapsed, isMobile, onClose, limits, handleNavClick))}

        {/* ── FINANCIAL section (Inventory, Sales, Expenses) ── */}
        {canAccessFinancialModules(user?.role) && !collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400 px-3 pb-1.5 pt-3">
            {t('navigation.financial', 'Financial')}
          </p>
        )}
        {FINANCIAL_ITEMS.filter((item) => !item.financial || canAccessFinancialModules(user?.role)).map((item) =>
          renderNavItem(item, collapsed, isMobile, onClose, limits, handleNavClick)
        )}

        {/* ── HR section (Employees, Attendance, Payroll, Reports) ── */}
        {canAccessFinancialModules(user?.role) && !collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-400 px-3 pb-1.5 pt-3">
            {t('navigation.hr', 'HR & Payroll')}
          </p>
        )}
        {HR_ITEMS.filter((item) => !item.financial || canAccessFinancialModules(user?.role)).map((item) =>
          renderNavItem(item, collapsed, isMobile, onClose, limits, handleNavClick)
        )}
      </nav>

      {/* ── Notifications shortcut ────────────────────────────── */}
      {/* <div className={`border-t border-primary-800/60 ${collapsed ? 'px-2 py-3 flex justify-center' : 'px-2 py-2'}`}>
        <button
          onClick={() => { onNotifClick(); if (isMobile()) onClose(); }}
          className={`relative transition-colors ${
            collapsed
              ? 'w-12 h-12 rounded-xl flex items-center justify-center text-primary-400 hover:bg-white/10 hover:text-white'
              : 'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-primary-200 hover:bg-white/10 hover:text-white'
          }`}
          title={collapsed ? 'Notifications' : undefined}
        >
          <Bell size={18} className="flex-shrink-0" />
          {!collapsed && <span>Notifications</span>}
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-accent-500 rounded-full" />
        </button>
      </div> */}

      {/* ── Profile link (desktop only) ───────────────────────── */}
      {/* <div className={`hidden lg:block border-t border-primary-800/60 ${collapsed ? 'px-2 py-2 flex justify-center' : 'px-2 py-2'}`}>
        <NavLink
          to="/profile"
          onClick={() => { if (isMobile()) onClose(); }}
          title={collapsed ? 'My Profile' : undefined}
          className={({ isActive }) =>
            collapsed
              ? `w-12 h-12 rounded-xl flex items-center justify-center border transition-all duration-150 ${
                  isActive
                    ? 'bg-white/20 border-white/25 text-white'
                    : 'border-transparent text-primary-400 hover:bg-white/10 hover:text-white'
                }`
              : `flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive ? 'bg-white/20 text-white' : 'text-primary-200 hover:bg-white/10 hover:text-white'
                }`
          }
        >
          <UserCircle size={18} className="flex-shrink-0" />
          {!collapsed && <span>My Profile</span>}
        </NavLink>
      </div> */}

      {/* ── Farm summary strip (expanded only) ───────────────── */}
      {/* {!collapsed && (
        <div className="px-3 pb-3">
          <div className="bg-white/8 rounded-xl px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary-700 flex items-center justify-center text-sm">🌾</div>
              <div className="min-w-0">
                <p className="text-white text-xs font-semibold truncate">My Farm</p>
                <p className="text-primary-300 text-[10px]">Owner · Free Plan</p>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* ── Copyright (expanded only, desktop) ──────────────── */}
      {!collapsed && (
        <div className="hidden lg:block px-3 pb-2">
          <p className="text-[9px] text-primary-500/70 text-center leading-relaxed">
            &copy; {new Date().getFullYear()} S2V Technology &middot;{' '}
            <Link to="/privacy" className="hover:text-primary-300 transition-colors">{t('profile.privacyPolicy')}</Link>
          </p>
          {/* <p className="text-[9px] text-primary-500/50 text-center mt-1">
            v{appVersion}
          </p> */}
        </div>
      )}

      {/* ── Collapse toggle (desktop only) ───────────────────── */}
      <div className="hidden lg:block border-t border-primary-800/60 p-2">
        <button
          onClick={onToggleCollapse}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-primary-400 hover:bg-white/10 hover:text-white transition-colors text-sm ${collapsed ? 'justify-center' : ''
            }`}
        >
          {collapsed ? (
            <ChevronRight size={16} />
          ) : (
            <>
              <ChevronLeft size={16} />
              <span>{t('navigation.collapse')}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
